import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Calendar, Clock, MapPin } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { FadeIn } from "@/components/common/FadeIn";
import { ArtworkModal } from "@/components/common/ArtworkModal";
import { SEO } from "@/components/common/SEO";
import { SITE_URL, GALLERY_INFO, HERO_IMAGE_URL, cloudinaryHero } from "@/lib/seo-config";
import { formatDate } from "@/lib/utils-date";
import api from "@/lib/api";

// Responsive hero — sourced from Cloudinary's fetch CDN with f_auto/q_auto.
// Original asset is ~6.69 MB; these variants weigh in at 75 KB / 199 KB /
// 409 KB / 593 KB respectively (mobile → 2x retina desktop).
const HERO_SRC_2400 = cloudinaryHero(2400);
const HERO_SRC_1920 = cloudinaryHero(1920);
const HERO_SRC_1280 = cloudinaryHero(1280);
const HERO_SRC_800 = cloudinaryHero(800);
const HERO_SRCSET = `${HERO_SRC_800} 800w, ${HERO_SRC_1280} 1280w, ${HERO_SRC_1920} 1920w, ${HERO_SRC_2400} 2400w`;

function UpcomingEvent({ event }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const eventDate = new Date(event.date + "T00:00:00");
  const day = eventDate.getDate();
  const month = eventDate.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const year = eventDate.getFullYear();

  return (
    <section ref={ref} className="pt-10 md:pt-14 pb-16 md:pb-24 px-6 lg:px-12" data-testid="upcoming-event-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-14 items-center">
          <motion.div
            initial={{ opacity: 0.6, y: 15 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.6, y: 15 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="relative"
          >
            <div className="absolute -top-4 -left-4 w-full h-full bg-[#0A1128]" />
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={event.image} alt={`${event.title} at Rain Boy Fine Art`} className="w-full h-full object-cover" />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0.6, y: 15 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.6, y: 15 }}
            transition={{ duration: 0.6, delay: 0.12, ease: "easeOut" }}
          >
            <p className="overline mb-4">Upcoming Event</p>

            <div className="flex items-start gap-6 mb-6">
              <div className="relative flex-shrink-0 w-20 h-24 flex flex-col items-center justify-center">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#CBA365]" />
                <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#CBA365]" />
                <span className="text-4xl font-heading text-[#0A1128] leading-none">{day}</span>
                <span className="text-xs tracking-[0.2em] text-[#CBA365] mt-1">{month}</span>
                <span className="text-[10px] text-[#8A94A6]">{year}</span>
              </div>

              <div>
                <h2 className="font-heading text-2xl sm:text-3xl text-[#0A1128] mb-3 leading-tight">{event.title}</h2>
                <p className="text-[#4A5568] leading-relaxed text-sm max-w-md">{event.description}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-6 mb-8 text-sm">
              <span className="flex items-center gap-2 text-[#CBA365]">
                <Clock size={13} />
                <span className="text-[#4A5568]">{event.time}{event.end_time ? ` - ${event.end_time}` : ""}</span>
              </span>
              {event.location && (
                <span className="flex items-center gap-2 text-[#CBA365]">
                  <MapPin size={13} />
                  <span className="text-[#4A5568]">Rain Boy Fine Art, Tulsa</span>
                </span>
              )}
            </div>

            <Link
              to={`/events/${event.slug || event.id}`}
              data-testid="event-cta-button"
              className="inline-flex items-center gap-3 border border-[#CBA365] text-[#0A1128] px-8 py-4 text-xs uppercase tracking-[0.2em] btn-fill-hover hover:text-white transition-all duration-300"
              style={{ '--fill-color': '#CBA365' }}
            >
              Event Details <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function FeaturedWorksMarquee({ artworks, onSelectArtwork }) {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [thumbStyle, setThumbStyle] = useState({ width: '20%', left: '0%' });

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    const visibleRatio = clientWidth / scrollWidth;
    const thumbW = Math.max(visibleRatio * 100, 8);
    const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * (100 - thumbW);
    setThumbStyle({ width: `${thumbW}%`, left: `${thumbLeft}%` });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, artworks]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector('[data-card]');
    if (!card) return;
    const distance = card.offsetWidth + 56;
    el.scrollBy({ left: dir * distance, behavior: 'smooth' });
  };

  // Drag to scroll
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);

  const onPointerDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    scrollStart.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
  };
  const onPointerMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - startX.current;
    scrollRef.current.scrollLeft = scrollStart.current - dx;
  };
  const onPointerUp = () => {
    isDragging.current = false;
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = '';
    }
  };

  return (
    <div className="relative">
      {/* Header */}
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 mb-10">
        <FadeIn>
          <div className="flex items-end justify-between">
            <div>
              <p className="overline mb-4">Collection</p>
              <h2 className="section-heading font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0A1128]">
                Featured Works
              </h2>
            </div>
            <Link
              to="/artists"
              className="group hidden sm:inline-flex items-center gap-2 mb-1"
              style={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.72rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#0A1128', textDecoration: 'none' }}
              data-testid="view-all-works"
            >
              <span className="border-b border-[#CBA365] pb-[2px] transition-colors duration-300 group-hover:text-[#CBA365]">View All</span>
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[#CBA365]" />
            </Link>
          </div>
        </FadeIn>
      </div>

      {/* Scroll area with arrows */}
      <FadeIn>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 relative">
        {/* Left arrow */}
        <button
          onClick={() => scroll(-1)}
          disabled={!canScrollLeft}
          data-testid="scroll-left"
          className="absolute -left-2 lg:left-0 z-20 w-[52px] h-[52px] border border-[#CBA365] bg-white/70 backdrop-blur-[8px] flex items-center justify-center transition-all duration-[350ms] hover:bg-[#0A1128] hover:border-[#0A1128] hover:-translate-x-1 active:-translate-x-1.5 group/btn hidden md:flex"
          style={{ top: 'calc(40% - 26px)', opacity: canScrollLeft ? 1 : 0, pointerEvents: canScrollLeft ? 'auto' : 'none', transition: 'opacity 0.35s, background-color 0.35s, border-color 0.35s, transform 0.35s cubic-bezier(0.25, 0.8, 0.4, 1)' }}
        >
          <ChevronLeft size={18} strokeWidth={1.25} className="text-[#0A1128] transition-all duration-300 group-hover/btn:text-[#CBA365] group-hover/btn:-translate-x-[2px]" />
        </button>

        {/* Right arrow */}
        <button
          onClick={() => scroll(1)}
          disabled={!canScrollRight}
          data-testid="scroll-right"
          className="absolute -right-2 lg:right-0 z-20 w-[52px] h-[52px] border border-[#CBA365] bg-white/70 backdrop-blur-[8px] flex items-center justify-center transition-all duration-[350ms] hover:bg-[#0A1128] hover:border-[#0A1128] hover:translate-x-1 active:translate-x-1.5 group/btn hidden md:flex"
          style={{ top: 'calc(40% - 26px)', opacity: canScrollRight ? 1 : 0, pointerEvents: canScrollRight ? 'auto' : 'none', transition: 'opacity 0.35s, background-color 0.35s, border-color 0.35s, transform 0.35s cubic-bezier(0.25, 0.8, 0.4, 1)' }}
        >
          <ChevronRight size={18} strokeWidth={1.25} className="text-[#0A1128] transition-all duration-300 group-hover/btn:text-[#CBA365] group-hover/btn:translate-x-[2px]" />
        </button>

        {/* Scrollable row */}
        <div
          ref={scrollRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          className="flex gap-[2rem] md:gap-[3.5rem] overflow-x-auto cursor-grab featured-works-scroll"
          style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          <style>{`.featured-works-scroll::-webkit-scrollbar { display: none; }`}</style>
          {artworks.map((artwork, i) => (
            <button
              key={artwork.id}
              data-card
              onClick={() => { if (!isDragging.current) onSelectArtwork(artwork); }}
              className="group flex-shrink-0 text-left"
              style={{ width: 'clamp(240px, 28vw, 440px)', scrollSnapAlign: 'start' }}
              data-testid={`artwork-card-${i}`}
            >
              <div className="aspect-[4/5] overflow-hidden mb-4 bg-[#F8F9FA]">
                <img
                  src={artwork.image}
                  alt={`${artwork.title} by ${artwork.artist_name}`}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  draggable={false}
                />
              </div>
              <div className="transition-transform duration-500 ease-out group-hover:-translate-y-1">
                <h3 className="font-heading text-lg text-[#0A1128] mb-1 relative inline-block">
                  {artwork.title}
                  <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-[#CBA365] transition-all duration-500 ease-out group-hover:w-full" />
                </h3>
                <p className="font-heading italic mb-1" style={{ fontSize: '1rem', color: 'rgba(10, 25, 41, 0.75)', textTransform: 'none', WebkitTextStroke: '0.4px currentColor', textShadow: 'none', letterSpacing: '0' }}>
                  {artwork.artist_name}
                </p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400, fontSize: '0.7rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(10, 25, 41, 0.55)' }}>
                  {artwork.dimensions || artwork.medium || 'Mixed Media'}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Right edge fade */}
        <div
          className="absolute right-0 top-0 w-[60px] md:w-[120px] pointer-events-none z-10"
          style={{
            bottom: '2rem',
            background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,1) 100%)',
            opacity: canScrollRight ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        />
      </div>
      </FadeIn>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 mt-8">
        <div ref={trackRef} className="relative w-full h-[1px] bg-[rgba(10,25,41,0.1)]">
          <div
            className="absolute top-[-0.5px] h-[2px] bg-[#CBA365] transition-[left] duration-100 ease-out"
            style={thumbStyle}
          />
        </div>
        <p className="mt-3" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400, fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(10, 25, 41, 0.4)' }}>
          Drag, swipe, or scroll to explore
        </p>
      </div>

      {/* Mobile View All link */}
      <div className="sm:hidden px-6 mt-6">
        <Link
          to="/artists"
          className="group inline-flex items-center gap-2"
          style={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.72rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#0A1128' }}
        >
          <span className="border-b border-[#CBA365] pb-[2px] transition-colors duration-300 group-hover:text-[#CBA365]">View All</span>
          <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[#CBA365]" />
        </Link>
      </div>
    </div>
  );
}

function FeaturedArtistsMarquee({ artists }) {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [thumbStyle, setThumbStyle] = useState({ width: '20%', left: '0%' });

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    const visibleRatio = clientWidth / scrollWidth;
    const thumbW = Math.max(visibleRatio * 100, 8);
    const denom = scrollWidth - clientWidth;
    const thumbLeft = denom > 0 ? (scrollLeft / denom) * (100 - thumbW) : 0;
    setThumbStyle({ width: `${thumbW}%`, left: `${thumbLeft}%` });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, artists]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector('[data-card]');
    if (!card) return;
    const distance = card.offsetWidth + 56;
    el.scrollBy({ left: dir * distance, behavior: 'smooth' });
  };

  // Drag-to-scroll support, mirroring FeaturedWorksMarquee behaviour.
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);

  const onPointerDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    scrollStart.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
  };
  const onPointerMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - startX.current;
    scrollRef.current.scrollLeft = scrollStart.current - dx;
  };
  const onPointerUp = () => {
    isDragging.current = false;
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = '';
    }
  };

  // If the gallery has 4 or fewer featured artists, the row fits on
  // desktop without overflow — the arrows + progress track are hidden
  // since there's nothing to scroll. The drag handlers stay attached
  // for touch users on narrow viewports.
  const showScrollControls = artists.length > 4;

  return (
    <div className="relative">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 mb-10">
        <FadeIn>
          <div className="flex items-end justify-between">
            <div>
              <p className="overline mb-4">Our Artists</p>
              <h2 className="section-heading font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0A1128]">
                Featured Artists
              </h2>
            </div>
            <Link
              to="/artists"
              className="group hidden sm:inline-flex items-center gap-2 mb-1"
              style={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.72rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#0A1128', textDecoration: 'none' }}
              data-testid="view-all-artists"
            >
              <span className="border-b border-[#CBA365] pb-[2px] transition-colors duration-300 group-hover:text-[#CBA365]">View All</span>
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[#CBA365]" />
            </Link>
          </div>
        </FadeIn>
      </div>

      <FadeIn>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 relative">
        <button
          onClick={() => scroll(-1)}
          disabled={!canScrollLeft}
          data-testid="artists-scroll-left"
          className="absolute -left-2 lg:left-0 z-20 w-[52px] h-[52px] border border-[#CBA365] bg-white/70 backdrop-blur-[8px] flex items-center justify-center transition-all duration-[350ms] hover:bg-[#0A1128] hover:border-[#0A1128] hover:-translate-x-1 active:-translate-x-1.5 group/btn hidden md:flex"
          style={{ top: 'calc(40% - 26px)', opacity: canScrollLeft && showScrollControls ? 1 : 0, pointerEvents: canScrollLeft && showScrollControls ? 'auto' : 'none', transition: 'opacity 0.35s, background-color 0.35s, border-color 0.35s, transform 0.35s cubic-bezier(0.25, 0.8, 0.4, 1)' }}
        >
          <ChevronLeft size={18} strokeWidth={1.25} className="text-[#0A1128] transition-all duration-300 group-hover/btn:text-[#CBA365] group-hover/btn:-translate-x-[2px]" />
        </button>

        <button
          onClick={() => scroll(1)}
          disabled={!canScrollRight}
          data-testid="artists-scroll-right"
          className="absolute -right-2 lg:right-0 z-20 w-[52px] h-[52px] border border-[#CBA365] bg-white/70 backdrop-blur-[8px] flex items-center justify-center transition-all duration-[350ms] hover:bg-[#0A1128] hover:border-[#0A1128] hover:translate-x-1 active:translate-x-1.5 group/btn hidden md:flex"
          style={{ top: 'calc(40% - 26px)', opacity: canScrollRight && showScrollControls ? 1 : 0, pointerEvents: canScrollRight && showScrollControls ? 'auto' : 'none', transition: 'opacity 0.35s, background-color 0.35s, border-color 0.35s, transform 0.35s cubic-bezier(0.25, 0.8, 0.4, 1)' }}
        >
          <ChevronRight size={18} strokeWidth={1.25} className="text-[#0A1128] transition-all duration-300 group-hover/btn:text-[#CBA365] group-hover/btn:translate-x-[2px]" />
        </button>

        <div
          ref={scrollRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          className="flex gap-[2rem] md:gap-[3.5rem] overflow-x-auto cursor-grab featured-artists-scroll"
          style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          <style>{`.featured-artists-scroll::-webkit-scrollbar { display: none; }`}</style>
          {artists.map((artist) => (
            <Link
              key={artist.id}
              to={`/artists/${artist.slug}`}
              data-card
              onClick={(e) => { if (isDragging.current) e.preventDefault(); }}
              className="group flex-shrink-0 text-left"
              style={{ width: 'clamp(240px, 28vw, 440px)', scrollSnapAlign: 'start' }}
              data-testid={`artist-card-${artist.slug}`}
            >
              <div className="aspect-square overflow-hidden mb-4 bg-[#F8F9FA]">
                <img
                  src={artist.photo}
                  alt={`Portrait of ${artist.name}, artist at Rain Boy Fine Art`}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  draggable={false}
                />
              </div>
              <div className="transition-transform duration-500 ease-out group-hover:-translate-y-1">
                <h3 className="font-heading text-lg text-[#0A1128] mb-1 relative inline-block">
                  {artist.name}
                  <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-[#CBA365] transition-all duration-500 ease-out group-hover:w-full" />
                </h3>
                <p className="text-sm text-[#8A94A6]">{artist.medium}</p>
              </div>
            </Link>
          ))}
        </div>

        <div
          className="absolute right-0 top-0 w-[60px] md:w-[120px] pointer-events-none z-10"
          style={{
            bottom: '2rem',
            background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(248,249,250,0.6) 50%, rgba(248,249,250,1) 100%)',
            opacity: canScrollRight && showScrollControls ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        />
      </div>
      </FadeIn>

      {showScrollControls && (
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 mt-8">
          <div ref={trackRef} className="relative w-full h-[1px] bg-[rgba(10,25,41,0.1)]">
            <div
              className="absolute top-[-0.5px] h-[2px] bg-[#CBA365] transition-[left] duration-100 ease-out"
              style={thumbStyle}
            />
          </div>
          <p className="mt-3" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400, fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(10, 25, 41, 0.4)' }}>
            Drag, swipe, or scroll to explore
          </p>
        </div>
      )}

      <div className="sm:hidden px-6 mt-6">
        <Link
          to="/artists"
          className="group inline-flex items-center gap-2"
          style={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.72rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#0A1128' }}
        >
          <span className="border-b border-[#CBA365] pb-[2px] transition-colors duration-300 group-hover:text-[#CBA365]">View All</span>
          <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[#CBA365]" />
        </Link>
      </div>
    </div>
  );
}


export default function Home() {
  const [artists, setArtists] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  useEffect(() => {
    if (selectedArtwork) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [selectedArtwork]);

  useEffect(() => {
    api.get("/artists?featured=true").then((r) => setArtists(r.data));
    api.get("/artworks?featured=true").then((r) => setArtworks(r.data.slice(0, 6)));
    api.get("/events").then((r) => {
      const upcoming = r.data.filter((e) => !e.is_past);
      if (upcoming.length) setNextEvent(upcoming[0]);
    });
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Rain Boy Fine Art | Contemporary Art Gallery in Tulsa, OK"
        description="Rain Boy Fine Art is a contemporary art gallery in Tulsa, OK featuring original work from emerging and established artists. Visit the gallery or browse our collection online."
        path="/"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "ArtGallery",
            "@id": "https://rainboyfineart.com/#gallery",
            name: "Rain Boy Fine Art",
            description: "Contemporary fine art gallery in Tulsa, OK featuring original work from emerging and established artists.",
            url: "https://rainboyfineart.com",
            image: HERO_IMAGE_URL,
            priceRange: "$$-$$$",
            address: { "@type": "PostalAddress", streetAddress: GALLERY_INFO.streetAddress, addressLocality: "Tulsa", addressRegion: "OK", postalCode: GALLERY_INFO.zip, addressCountry: "US" },
            telephone: GALLERY_INFO.phone,
            geo: { "@type": "GeoCoordinates", latitude: 36.149478506924474, longitude: -96.0284077165505 },
            openingHoursSpecification: GALLERY_INFO.hours.map(h => ({ "@type": "OpeningHoursSpecification", dayOfWeek: h.days, opens: h.opens, closes: h.closes })),
            sameAs: GALLERY_INFO.socials,
          },
          { "@context": "https://schema.org", "@type": "WebSite", name: "Rain Boy Fine Art", url: "https://rainboyfineart.com", description: "Contemporary fine art gallery in Tulsa, OK." },
        ]}
      />
      {/* Hero */}
      <section className="relative h-screen overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_SRC_2400}
            srcSet={HERO_SRCSET}
            sizes="100vw"
            alt="Rain Boy Fine Art gallery interior in Tulsa, OK"
            className="w-full h-full object-cover ken-burns"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-[#0A1128]/20" />
        </div>
        <div className="relative z-10 flex items-end h-full px-6 md:px-12 lg:px-16 pb-12 md:pb-16 lg:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            <div className="relative bg-[#0A1128] px-8 sm:px-12 md:px-14 py-10 sm:py-12 md:py-14 max-w-lg">
              <p className="overline mb-5 inline-block">Rain Boy Fine Art &middot; Tulsa</p>

              <h1 className="section-heading font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-6">
                Contemporary Native + American Art
              </h1>

              <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-8 max-w-sm" style={{ textAlign: 'left' }}>
                World-class contemporary and Indigenous artistry, intimately curated in the heart of Tulsa.
              </p>

              <Link
                to="/artists"
                data-testid="hero-cta-button"
                className="hero-text-wipe inline-flex relative overflow-hidden bg-[#CBA365] text-[#0A1128] px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium"
              >
                <span className="inline-flex items-center gap-3">
                  Explore the Gallery <ArrowRight size={14} />
                </span>
                <span className="hero-text-wipe__top inline-flex items-center gap-3 text-white" aria-hidden="true">
                  Explore the Gallery <ArrowRight size={14} />
                </span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Event (with leading spacer). Both render only when an
          event exists, so when no event is scheduled there's no orphaned
          white strip between the marquee and Featured Artists. */}
      {nextEvent && (
        <>
          <div className="w-full py-4 md:py-6" />
          <UpcomingEvent event={nextEvent} />
        </>
      )}

      {/* Featured Artists - Horizontal Marquee */}
      <section className="bg-[#F8F9FA] py-16 md:py-24 overflow-hidden" data-testid="featured-artists-section">
        <FeaturedArtistsMarquee artists={artists} />
      </section>

      {/* Featured Artwork - Horizontal Marquee */}
      <section className="pt-20 pb-16 md:pt-20 md:pb-16 overflow-hidden" data-testid="featured-artwork-section">
        <FeaturedWorksMarquee artworks={artworks} onSelectArtwork={setSelectedArtwork} />
      </section>

      {selectedArtwork && (
        <ArtworkModal
          artwork={selectedArtwork}
          onClose={() => setSelectedArtwork(null)}
        />
      )}
    </div>
  );
}
